import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconShieldLock, IconTruck } from '@tabler/icons-react';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { useTouring } from 'components/self-tour/hooks/useTouring';

import { BuyDirectContextModal } from './PropertySection/BuyDirectContextModal';
import { SelfTourAppHandoffModal } from './PropertySection/SelfTourAppHandoffModal';
import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';

type TourAction = NonNullable<ReturnType<typeof useTouring>>['primaryAction'];
type BuyDirectCTAProperty = Pick<
  MarketplaceProperty,
  | 'addressToken'
  | 'buyDirectPrice'
  | 'buyDirectSavings'
  | 'canSelfTour'
  | 'displayBadge'
  | 'hasCurrentAvailability'
  | 'listingState'
  | 'listingTags'
  | 'slug'
>;

// atoms Button splits link vs button by `href`, so an action with an href (and
// not disabled) renders as an anchor; everything else renders as a button.
const renderTourAction = (action: TourAction) => (
  <Button
    analyticsName={action.analyticsName}
    data-analytics-name={action.analyticsName}
    aria-label={action.text}
    variant={action.variant}
    size="lg"
    onClick={action.onClick}
    disabled={action.disabled}
    href={action.disabled ? undefined : action.href}
    target={action.target}
    rel="noopener"
    className="w-full"
  >
    {action.text}
  </Button>
);

export const BuyDirectCTA = ({ property }: { property: BuyDirectCTAProperty }) => {
  const touringInfo = useTouring(property, { placement: 'main' });

  if (!touringInfo) {
    return null;
  }

  const {
    primaryAction,
    secondaryAction,
    tourKind,
    showBuyDirect,
    isDialogOpen,
    closeDialog,
    isSelfTourDialogOpen,
    closeSelfTourDialog,
    isBuyDirectContextOpen,
    closeBuyDirectContext,
    buyDirectContextHref,
  } = touringInfo;

  if (!showBuyDirect || property.buyDirectPrice == null) {
    const isTourWithAgent = tourKind === 'tourWithAgent';
    return (
      <>
        <Col className="gap-7 rounded-2xl border border-od-border-primary bg-od-bg-primary px-5 py-6 shadow-[0px_10px_22px_0px_rgba(0,0,0,0.04),0px_2px_12px_0px_rgba(0,0,0,0.04)]">
          <Col className="gap-2">
            <Text as="p" className="text-subheader-xs text-od-content-primary">
              Take a tour
            </Text>
            <Text as="p" className="text-body-md text-od-content-secondary">
              {isTourWithAgent
                ? 'Share this home with your agent and have them arrange a time to tour the home.'
                : 'Unlock the door with the Opendoor app yourself, no agent needed.'}
            </Text>
          </Col>
          <Col className="items-center gap-3">{renderTourAction(primaryAction)}</Col>
        </Col>
        {isTourWithAgent && (
          <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
        )}
        <SelfTourAppHandoffModal
          isOpen={isSelfTourDialogOpen}
          onClose={closeSelfTourDialog}
          placement="main"
          addressToken={property.addressToken}
          slug={property.slug}
        />
      </>
    );
  }

  const discount = property.buyDirectSavings ?? 0;
  const showDiscount = discount > 0;

  return (
    <>
      <Col className="gap-6 rounded-2xl border border-od-border-primary bg-od-bg-primary px-6 py-6 shadow-[0px_10px_22px_0px_rgba(0,0,0,0.04),0px_2px_12px_0px_rgba(0,0,0,0.04)]">
        <Col className="gap-3">
          <Text as="p" className="text-label-lg text-od-content-primary">
            Buy with Opendoor
          </Text>
          <Row className="items-center gap-4">
            <Text as="h3" className="text-header-sm text-od-content-primary">
              {formatCurrency(property.buyDirectPrice)}
            </Text>
          </Row>
          {showDiscount && (
            <Box className="self-start rounded-md bg-[color-mix(in_srgb,var(--color-od-bg-success)_10%,white)] px-2.5 py-0.5">
              <Text as="span" className="text-body-sm font-medium text-od-content-success">
                {formatCurrency(discount)} below list
              </Text>
            </Box>
          )}
        </Col>

        <Row className="hidden justify-between gap-4 lg:flex">
          <Row className="items-center gap-2">
            <IconTruck size={20} color="var(--color-od-content-primary)" />
            <Text as="p" className="text-body-sm text-od-content-primary">
              Early Move-In
            </Text>
          </Row>
          <Row className="items-center gap-2">
            <IconShieldLock size={20} color="var(--color-od-content-primary)" />
            <Text as="p" className="text-body-sm text-od-content-primary">
              100-Day Warranty
            </Text>
          </Row>
          <Row className="items-center gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.1499 2.95386C15.3007 2.95389 16.2337 3.88712 16.2339 5.03784V9.84644C16.2482 9.89887 16.2583 9.95354 16.2583 10.0105C16.2583 10.0672 16.2481 10.1214 16.2339 10.1736V14.99C16.2338 16.1409 15.3008 17.0739 14.1499 17.074H5.85791C4.70702 17.074 3.77397 16.1409 3.77393 14.99V5.03784C3.77413 3.88709 4.70712 2.95386 5.85791 2.95386H14.1499ZM5.85791 4.20386C5.39767 4.20386 5.02413 4.57765 5.02393 5.03784V14.99L5.02783 15.075C5.06757 15.4673 5.37973 15.7791 5.77197 15.8191L5.85791 15.823H14.1499C14.5815 15.823 14.9364 15.4953 14.979 15.075L14.9839 14.99V10.6355H9.89307L12.8052 13.5466C13.0493 13.7908 13.0493 14.1873 12.8052 14.4314C12.561 14.6754 12.1645 14.6754 11.9204 14.4314L7.94189 10.4529C7.69779 10.2088 7.69787 9.81227 7.94189 9.56812L11.9204 5.5896C12.1646 5.34545 12.561 5.34545 12.8052 5.5896C13.0493 5.83375 13.0493 6.23024 12.8052 6.47437L9.89307 9.3855H14.9839V5.03784C14.9837 4.57767 14.6101 4.20389 14.1499 4.20386H5.85791Z"
                fill="var(--color-od-content-primary)"
              />
            </svg>
            <Text as="p" className="text-body-sm text-od-content-primary">
              Free Cancellation
            </Text>
          </Row>
        </Row>

        <Col className="items-center gap-3">
          <Col className="w-full gap-4">
            {secondaryAction && renderTourAction(secondaryAction)}
            {renderTourAction(primaryAction)}
          </Col>
        </Col>
      </Col>
      {tourKind === 'tourWithAgent' && (
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
      )}
      <SelfTourAppHandoffModal
        isOpen={isSelfTourDialogOpen}
        onClose={closeSelfTourDialog}
        placement="main"
        addressToken={property.addressToken}
        slug={property.slug}
      />
      <BuyDirectContextModal
        isOpen={isBuyDirectContextOpen}
        onClose={closeBuyDirectContext}
        continueHref={buyDirectContextHref}
      />
    </>
  );
};
